import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Search, X, Save, Mail, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/adminApi";

const TYPE_LABEL = { quick: "Quick", quote: "Quote", contact: "Contact" };
const STATUSES = ["new", "contacted", "won", "lost"];
const STATUS_BADGE = {
  new: "bg-ridge-amber/15 text-ridge-amber-dark",
  contacted: "bg-blue-100 text-blue-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-zinc-200 text-zinc-700",
};

export default function AdminLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("selected");

  const [filters, setFilters] = useState(() => ({
    type: searchParams.get("type") || "",
    status: searchParams.get("status") || "",
    q: searchParams.get("q") || "",
    date_from: searchParams.get("date_from") || "",
    date_to: searchParams.get("date_to") || "",
  }));
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [resp, setResp] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await adminApi.listLeads({ ...filters, page, page_size: pageSize });
    if (res.ok) setResp(res.data);
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const exportUrl = useMemo(() => adminApi.exportCsvUrl(filters), [filters]);

  const onApply = (e) => {
    e?.preventDefault();
    setPage(1);
    load();
  };
  const onReset = () => {
    setFilters({ type: "", status: "", q: "", date_from: "", date_to: "" });
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(resp.total / pageSize));

  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("selected");
    setSearchParams(next, { replace: true });
  };
  const openLead = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("selected", id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="admin-leads-page">
      <header className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <span className="overline">Inbox</span>
          <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-ridge-charcoal md:text-4xl">
            Leads
          </h1>
          <p className="mt-2 text-sm text-ridge-muted">
            {resp.total.toLocaleString()} total · showing page {page} of {totalPages}
          </p>
        </div>
        <a
          href={exportUrl}
          className="btn-secondary"
          data-testid="admin-leads-export"
          target="_blank"
          rel="noreferrer"
        >
          <Download className="h-4 w-4" /> Export CSV
        </a>
      </header>

      {/* Filter toolbar */}
      <form
        onSubmit={onApply}
        className="mt-8 grid grid-cols-2 gap-3 rounded-md border border-ridge-border bg-white p-4 md:grid-cols-6"
        data-testid="admin-leads-filters"
      >
        <div className="col-span-2 md:col-span-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ridge-muted" />
            <input
              type="search"
              placeholder="Search name, email or message…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="admin-leads-search"
              className="w-full rounded-md border border-ridge-border py-2.5 pl-9 pr-3 text-sm focus:border-ridge-charcoal focus:outline-none focus:ring-2 focus:ring-ridge-charcoal/15"
            />
          </div>
        </div>
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          data-testid="admin-leads-type-filter"
          className="rounded-md border border-ridge-border px-3 py-2.5 text-sm focus:border-ridge-charcoal focus:outline-none"
        >
          <option value="">All types</option>
          <option value="quick">Quick</option>
          <option value="quote">Quote</option>
          <option value="contact">Contact</option>
          <option value="chat">Chat</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          data-testid="admin-leads-status-filter"
          className="rounded-md border border-ridge-border px-3 py-2.5 text-sm focus:border-ridge-charcoal focus:outline-none"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          data-testid="admin-leads-date-from"
          className="rounded-md border border-ridge-border px-3 py-2.5 text-sm focus:border-ridge-charcoal focus:outline-none"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          data-testid="admin-leads-date-to"
          className="rounded-md border border-ridge-border px-3 py-2.5 text-sm focus:border-ridge-charcoal focus:outline-none"
        />
        <div className="col-span-2 flex justify-end gap-2 md:col-span-6">
          <button type="button" onClick={onReset} className="text-xs font-semibold uppercase tracking-[0.14em] text-ridge-muted hover:text-ridge-charcoal">
            Reset
          </button>
          <button type="submit" className="btn-primary px-5 py-2 text-xs">
            Apply filters
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="mt-6 overflow-x-auto rounded-md border border-ridge-border bg-white">
        <table className="w-full min-w-[800px] text-sm" data-testid="admin-leads-table">
          <thead className="bg-ridge-stone/60 text-xs uppercase tracking-[0.14em] text-ridge-muted">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Date</th>
              <th className="px-4 py-3 text-left font-semibold">Type</th>
              <th className="px-4 py-3 text-left font-semibold">Name</th>
              <th className="px-4 py-3 text-left font-semibold">Email</th>
              <th className="px-4 py-3 text-left font-semibold">Subject / Service</th>
              <th className="px-4 py-3 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ridge-border">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-ridge-muted">Loading…</td></tr>
            ) : resp.items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-ridge-muted">No leads match those filters.</td></tr>
            ) : resp.items.map((lead) => (
              <tr
                key={lead.id}
                className="cursor-pointer hover:bg-ridge-stone/40"
                onClick={() => openLead(lead.id)}
                data-testid={`admin-lead-row-${lead.id}`}
              >
                <td className="whitespace-nowrap px-4 py-3 text-ridge-muted">{(lead.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <span className="rounded-md bg-ridge-stone px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ridge-charcoal">
                    {TYPE_LABEL[lead.type] || lead.type}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-ridge-charcoal">{lead.name}</td>
                <td className="px-4 py-3 text-ridge-text">{lead.email}</td>
                <td className="px-4 py-3 text-ridge-text">{lead.service_type || lead.subject || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-md px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-[0.12em] ${STATUS_BADGE[lead.status] || ""}`}>
                    {lead.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {resp.total > pageSize ? (
        <div className="mt-4 flex items-center justify-between text-sm text-ridge-muted">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-ridge-border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-md border border-ridge-border px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      {selectedId ? (
        <LeadDrawer leadId={selectedId} onClose={closeDrawer} onSaved={load} />
      ) : null}
    </div>
  );
}

function LeadDrawer({ leadId, onClose, onSaved }) {
  const [lead, setLead] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLead(null); setLoadErr(null);
    (async () => {
      const res = await adminApi.getLead(leadId);
      if (cancelled) return;
      if (res.ok) {
        setLead(res.data);
        setStatus(res.data.status || "new");
        setNotes(res.data.notes || "");
      } else setLoadErr(res.error);
    })();
    return () => { cancelled = true; };
  }, [leadId]);

  const save = async () => {
    setSaving(true);
    const res = await adminApi.patchLead(leadId, { status, notes });
    setSaving(false);
    if (res.ok) {
      setLead(res.data);
      onSaved?.();
    }
  };

  const mailto = lead
    ? `mailto:${lead.email}?subject=Re%3A%20your%20Ridgehawk%20Logistics%20${encodeURIComponent(TYPE_LABEL[lead.type] || lead.type)}%20request`
    : "#";

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" data-testid="admin-lead-drawer">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-black/40"
      />
      <aside className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl">
        <header className="sticky top-0 flex items-center justify-between border-b border-ridge-border bg-white px-5 py-4">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ridge-amber">Lead</span>
          <button type="button" onClick={onClose} aria-label="Close drawer" className="text-ridge-muted hover:text-ridge-charcoal" data-testid="admin-lead-drawer-close">
            <X className="h-5 w-5" />
          </button>
        </header>

        {loadErr ? (
          <p className="m-5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{loadErr}</p>
        ) : !lead ? (
          <p className="p-6 text-sm text-ridge-muted">Loading…</p>
        ) : (
          <div className="flex-1 space-y-6 px-5 py-5">
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight text-ridge-charcoal">{lead.name}</h2>
              <p className="mt-1 text-sm text-ridge-muted">
                {(TYPE_LABEL[lead.type] || lead.type)} · {(lead.created_at || "").replace("T", " ").slice(0, 16)}
              </p>
              <a
                href={mailto}
                className="btn-ghost-amber mt-3"
                data-testid="admin-lead-drawer-mailto"
              >
                <Mail className="h-3.5 w-3.5" /> Reply via email
              </a>
            </div>

            {/* AI qualification summary */}
            {lead.ai_summary ? (
              <div
                className="rounded-md border border-ridge-amber/40 bg-ridge-amber/5 p-4"
                data-testid="admin-lead-ai-summary"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-5 items-center rounded-full bg-ridge-amber px-2 text-[0.6rem] font-bold uppercase tracking-[0.18em] text-white">
                    AI
                  </span>
                  <span className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ridge-amber">
                    Qualification Summary
                  </span>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ridge-text">{lead.ai_summary}</p>
                {lead.ai_summary_generated_at ? (
                  <p className="mt-2 text-[0.65rem] uppercase tracking-[0.15em] text-ridge-muted">
                    Generated {(lead.ai_summary_generated_at || "").replace("T", " ").slice(0, 16)}
                  </p>
                ) : null}
              </div>
            ) : null}

            {/* Status + notes */}
            <div className="rounded-md border border-ridge-border bg-ridge-stone p-4">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="admin-lead-drawer-status"
                className="block w-full rounded-md border border-ridge-border bg-white px-3 py-2 text-sm focus:border-ridge-charcoal focus:outline-none"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <label className="mb-1.5 mt-4 block text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">
                Internal notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Notes only visible to admins…"
                data-testid="admin-lead-drawer-notes"
                className="block w-full rounded-md border border-ridge-border bg-white px-3 py-2 text-sm focus:border-ridge-charcoal focus:outline-none"
              />
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={save} disabled={saving} className="btn-primary px-4 py-2 text-xs" data-testid="admin-lead-drawer-save">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </button>
              </div>
            </div>

            {/* Detail kv */}
            <KV title="Contact">
              {[
                ["Email", lead.email],
                ["Phone", lead.phone],
                ["Company", lead.company],
                ["Subject", lead.subject],
              ]}
            </KV>
            <KV title="Logistics">
              {[
                ["Service", lead.service_type],
                ["Pickup", lead.pickup_city],
                ["Drop-off", lead.dropoff_city],
                ["Pickup Date", lead.pickup_date],
                ["Pickup Time", lead.pickup_time],
                ["Weight (kg)", lead.weight_kg],
                ["Cargo", lead.cargo_description],
                ["Special Reqs", lead.special_requirements],
                ["Message", lead.message],
              ]}
            </KV>
            <KV title="Source / Tracking">
              {[
                ["UTM Source", lead.utm_source],
                ["UTM Medium", lead.utm_medium],
                ["UTM Campaign", lead.utm_campaign],
                ["Referrer", lead.referrer],
                ["IP Hash", lead.ip_hash?.slice(0, 16) + "…"],
              ]}
            </KV>

            {(lead.audit_log || []).length > 0 ? (
              <div className="rounded-md border border-ridge-border bg-white p-4">
                <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">Audit log</h3>
                <ul className="mt-3 space-y-2 text-xs text-ridge-muted">
                  {lead.audit_log.map((entry, i) => (
                    <li key={i}>
                      {entry.at?.slice(0, 16)?.replace("T", " ")} · {entry.field} → <strong className="text-ridge-charcoal">{entry.to}</strong> · {entry.by}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </aside>
    </div>
  );
}

function KV({ title, children }) {
  const rows = (typeof children === "function" ? children() : children) || [];
  const filtered = rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!filtered.length) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-ridge-charcoal">{title}</h3>
      <dl className="mt-3 space-y-2 rounded-md border border-ridge-border bg-white p-4 text-sm">
        {filtered.map(([k, v]) => (
          <div key={k} className="grid grid-cols-3 gap-3">
            <dt className="text-xs font-semibold uppercase tracking-[0.1em] text-ridge-muted">{k}</dt>
            <dd className="col-span-2 break-words text-ridge-text">{String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
